import type { StoreApi } from "zustand";
import type { AttributeCollectionId, FieldId } from "../../types";
import type { FieldValueType } from "../../types";
import {
  removeCollectionErrors,
  removeCollectionFields,
} from "../../utils/field-collection-utils";
import { parseFieldId } from "../../utils/field-id";
import type {
  FormStore,
  OptionalCollectionActions,
  SliceSetFunction,
} from "../types";

/**
 * オプショナルコレクション機能スライス
 *
 * 主な責務:
 * - コレクションの入力スキップ状態の管理（有効/無効）
 * - minCount=0の動的コレクションを任意入力として扱う
 * - コレクション状態変更時の値・エラー処理
 */
export const createOptionalCollectionSlice = (
  set: SliceSetFunction,
  get: () => FormStore,
  _store: StoreApi<FormStore>,
): OptionalCollectionActions => ({
  /**
   * コレクションの入力スキップ状態を設定する
   * @param collectionId コレクションID
   * @param collectionIndex コレクションインデックス
   * @param enabled true: 入力する, false: 入力をスキップする
   */
  setCollectionEnabled: (
    collectionId: AttributeCollectionId,
    collectionIndex: number,
    enabled: boolean,
  ) => {
    set(
      (state) => {
        if (enabled) {
          // 【入力する】状態に変更: 無効リストから削除し、初期値を復元

          // 無効リストから削除
          const newDisabledCollections = state.disabledCollections.filter(
            (item) =>
              !(
                item.collectionId === collectionId &&
                item.collectionIndex === collectionIndex
              ),
          );

          // コレクションの初期値を復元する
          if (!state.initialValueProvider) {
            return state;
          }
          const resetValues = state.initialValueProvider.getInitialValues();
          const currentValues = [...state.values];

          // 関連するフィールドの初期値を探して現在の値に反映
          Object.entries(resetValues).forEach(([fieldId, initialValue]) => {
            const parsedId = parseFieldId(fieldId as FieldId);
            if (
              parsedId &&
              parsedId.collectionId === collectionId &&
              parsedId.collectionIndex === collectionIndex
            ) {
              // 同じフィールドIDの値が既に存在するか確認
              const existingValueIndex = currentValues.findIndex(
                (v) => v.fieldId === fieldId,
              );

              if (existingValueIndex >= 0) {
                // 既存の値を更新
                currentValues[existingValueIndex] = {
                  ...currentValues[existingValueIndex],
                  value: initialValue,
                };
              } else {
                // 新しい値を追加
                currentValues.push({
                  fieldId: fieldId as FieldId,
                  attributeId: parsedId.attributeId,
                  value: initialValue,
                });
              }
            }
          });

          return {
            disabledCollections: newDisabledCollections,
            values: currentValues,
          };
        } else {
          // 【入力をスキップする】状態に変更: 無効リストに追加し、値とエラーをクリア

          // 既に無効化されている場合は何もしない
          const alreadyDisabled = state.disabledCollections.some(
            (item) =>
              item.collectionId === collectionId &&
              item.collectionIndex === collectionIndex,
          );

          if (alreadyDisabled) {
            return state;
          }

          // 動的コレクションでminCount=0かどうかを確認
          const isOptionalCollection =
            state.isOptionalCollection?.(collectionId) ?? false;

          // 新しい値の配列を作成
          let newValues = [...state.values];

          if (isOptionalCollection) {
            // minCount=0の動的コレクションの場合は、関連するフィールドを完全に削除
            // これによりsubmit時に空配列が送信される
            newValues = removeCollectionFields(
              newValues,
              collectionId,
              collectionIndex,
            );
          } else {
            // 通常の無効化: 値をクリアするだけ（フィールド自体は保持）
            newValues = newValues.map((fieldValue) => {
              const parsedId = parseFieldId(fieldValue.fieldId);
              if (
                parsedId &&
                parsedId.collectionId === collectionId &&
                parsedId.collectionIndex === collectionIndex
              ) {
                // このコレクションに属するフィールドの値をクリア
                // 値の型によって適切な空の値を設定
                let emptyValue: FieldValueType = "";

                // 配列型の場合は空配列を設定
                if (Array.isArray(fieldValue.value)) {
                  emptyValue = [];
                }
                // オブジェクト型の場合は空オブジェクトを設定
                else if (
                  typeof fieldValue.value === "object" &&
                  fieldValue.value !== null
                ) {
                  emptyValue = {};
                }

                return {
                  ...fieldValue,
                  value: emptyValue,
                };
              }
              return fieldValue;
            });
          }

          // このコレクションのエラーもクリア
          const newErrors = removeCollectionErrors(
            state.errors,
            collectionId,
            collectionIndex,
          );

          return {
            disabledCollections: [
              ...state.disabledCollections,
              { collectionId, collectionIndex },
            ],
            values: newValues,
            errors: newErrors,
          };
        }
      },
      false,
      "optional/setCollectionEnabled",
    );
  },

  /**
   * コレクションが有効（入力する状態）かどうかを取得する
   * @returns true: 入力する状態, false: 入力をスキップする状態
   */
  isCollectionEnabled: (
    collectionId: AttributeCollectionId,
    collectionIndex: number,
  ): boolean => {
    const { disabledCollections } = get();
    return !disabledCollections.some(
      (item) =>
        item.collectionId === collectionId &&
        item.collectionIndex === collectionIndex,
    );
  },

  /**
   * コレクションが任意入力かどうかを判定する
   * 動的コレクションでminCount=0の場合は任意入力
   */
  isOptionalCollection: (collectionId: AttributeCollectionId): boolean => {
    const { form } = get();
    if (!form?.form?.attributeCollections) {
      return false;
    }

    const collection = form.form.attributeCollections.find(
      (c) => c.id === collectionId,
    );

    if (!collection) {
      return false;
    }

    // 動的コレクションでminCount=0の場合は任意入力
    return collection.isDynamic && collection.minCount === 0;
  },
});
